<template>
  <div class="page">
    <div class="bg-grid" />
    <div class="hero-glow" />

    <!-- NAVBAR -->
    <nav class="navbar" :class="{ scrolled: isScrolled }">
      <div class="nav-inner">
        <div class="logo">
          <div class="logo-mark" />
          <span class="logo-label">SMARTPROMPT</span>
        </div>
        <a href="/users/sign_in" class="sign-in-btn">SIGN IN</a>
      </div>
    </nav>

    <!-- HERO -->
    <section class="hero">
      <div class="container">
        <div class="eyebrow">
          <span class="eyebrow-tag">EARLY ACCESS · AI MIDDLEWARE</span>
        </div>
        <h1 class="headline">AI middleware that<br />improves itself.</h1>
        <p class="subheadline">
          SmartPrompt sits between your app and AI APIs. It manages prompts,
          learns from every call, and gets better automatically — no manual tuning.
        </p>

        <div v-if="!submitted" class="waitlist-form">
          <div class="form-row">
            <input
              v-model="email"
              type="email"
              class="email-input"
              placeholder="email@company.com"
              @keydown.enter="submitWaitlist"
              :disabled="submitting"
            />
            <button class="cta-btn" @click="submitWaitlist" :disabled="submitting">
              {{ submitting ? "JOINING..." : "JOIN THE WAITLIST →" }}
            </button>
          </div>
          <p v-if="formError" class="form-error">{{ formError }}</p>
          <p class="form-micro">No credit card · Early access · 2-minute setup</p>
        </div>

        <div v-else class="submitted-state">
          <span class="submitted-icon">✦</span>
          <p class="submitted-text">You're on the list! We'll be in touch before launch.</p>
        </div>

        <div class="waitlist-counter">
          <span class="counter-dot">✦</span>
          <span class="counter-text">{{ waitlistCount }} developers already on the waitlist</span>
        </div>
      </div>
    </section>

    <!-- SOCIAL PROOF STRIP -->
    <section class="proof-strip">
      <div class="container">
        <div class="proof-stats">
          <span class="proof-stat"><em>{{ waitlistCount }}</em> developers joined</span>
          <span class="proof-divider">·</span>
          <span class="proof-stat"><em>12</em> AI providers supported</span>
          <span class="proof-divider">·</span>
          <span class="proof-stat"><em>1</em> API call to replace them all</span>
        </div>
      </div>
    </section>

    <!-- THE PROBLEM -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <span class="section-label">THE PROBLEM</span>
        </div>
        <div class="problem-grid">
          <div class="problem-card">
            <span class="problem-label">SCATTERED PROMPTS</span>
            <p class="problem-text">
              Your prompts live in Slack, code comments, and spreadsheets. When production breaks,
              you don't know which version is running or how to roll back.
            </p>
          </div>
          <div class="problem-card">
            <span class="problem-label">NO FEEDBACK LOOP</span>
            <p class="problem-text">
              45% of AI responses have issues. You find out from users, not metrics. There's no
              systematic way to catch regressions or improve.
            </p>
          </div>
          <div class="problem-card">
            <span class="problem-label">RUNAWAY COSTS</span>
            <p class="problem-text">
              A 'simple' feature can consume 15,000+ tokens per call. Costs spiral at scale with
              no visibility into which features are expensive.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- THE SOLUTION -->
    <section class="section section-alt">
      <div class="container">
        <div class="section-header">
          <span class="section-label">HOW SMARTPROMPT FIXES IT</span>
        </div>
        <div class="bento-grid">
          <div class="bento-cell bento-wide">
            <span class="bento-label">AUTO-IMPROVING PROMPTS</span>
            <p class="bento-text">
              Human feedback triggers orchestration. Prompts are analyzed, tested against your
              data, and updated automatically.
            </p>
          </div>
          <div class="bento-cell bento-wide">
            <span class="bento-label">BUILT-IN EVAL SUITE</span>
            <p class="bento-text">
              Run your pipeline against labeled examples. Know your accuracy score before you
              ship. Catch regressions early.
            </p>
          </div>
          <div class="bento-cell">
            <span class="bento-label">LIVING RAG KNOWLEDGE BASE</span>
            <p class="bento-text">
              Every good response teaches the system. Learnings are stored, retrieved, and
              compounded automatically.
            </p>
          </div>
          <div class="bento-cell">
            <span class="bento-label">PIPELINE ORCHESTRATION</span>
            <p class="bento-text">
              Multi-step AI with ordered steps, self-critique loops, and JSON schema enforcement.
              Complex workflows made simple.
            </p>
          </div>
          <div class="bento-cell">
            <span class="bento-label">COST VISIBILITY</span>
            <p class="bento-text">
              Per-pipeline cost breakdown. Token analytics. Compare models. Know exactly what
              you're spending and where.
            </p>
          </div>
          <div class="bento-cell">
            <span class="bento-label">ANY MODEL, ANY PROVIDER</span>
            <p class="bento-text">
              Anthropic, OpenAI, Google. Route different steps to different models. Switch
              providers without touching your code.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <span class="section-label">GET STARTED IN MINUTES</span>
        </div>
        <div class="steps-grid">
          <div class="step">
            <div class="step-number">01</div>
            <div class="step-content">
              <span class="step-title">DESCRIBE YOUR PROJECT</span>
              <p class="step-text">
                Tell SmartPrompt what your product does. AI bootstraps your entire pipeline —
                steps, prompts, and feedback loops.
              </p>
            </div>
          </div>
          <div class="step-connector" />
          <div class="step">
            <div class="step-number">02</div>
            <div class="step-content">
              <span class="step-title">CONNECT YOUR APP</span>
              <p class="step-text">
                Replace your AI API calls with one SmartPrompt call. Pass your payload, get back
                structured output.
              </p>
            </div>
          </div>
          <div class="step-connector" />
          <div class="step">
            <div class="step-number">03</div>
            <div class="step-content">
              <span class="step-title">WATCH IT IMPROVE</span>
              <p class="step-text">
                Feedback flows in. The system evaluates, proposes changes, tests them, and applies
                improvements automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="section section-alt">
      <div class="container">
        <div class="section-header">
          <span class="section-label">COMMON QUESTIONS</span>
        </div>
        <div class="faq-list">
          <details class="faq-item">
            <summary class="faq-question">How is this different from calling OpenAI directly?</summary>
            <p class="faq-answer">
              Direct call = raw output, no memory, no improvement. SmartPrompt adds a managed
              pipeline that learns, self-evaluates, and improves — with no extra code from you.
            </p>
          </details>
          <details class="faq-item">
            <summary class="faq-question">Which AI providers are supported?</summary>
            <p class="faq-answer">
              Anthropic (Claude), OpenAI (GPT-4o, o1), Google (Gemini). Route different pipeline
              steps to different models. More adapters coming.
            </p>
          </details>
          <details class="faq-item">
            <summary class="faq-question">Do I need to rewrite my app?</summary>
            <p class="faq-answer">
              No. One endpoint change. You call <code>POST /api/v1/prompt</code> with your project
              UUID and payload. Everything else is managed for you.
            </p>
          </details>
          <details class="faq-item">
            <summary class="faq-question">What happens to my data?</summary>
            <p class="faq-answer">
              Your project data is fully isolated. No cross-project contamination. Tokens are
              encrypted at rest. Data is never used to train third-party models.
            </p>
          </details>
          <details class="faq-item">
            <summary class="faq-question">How much will it cost?</summary>
            <p class="faq-answer">
              We're working on pricing. Early access members will get a founder's plan — we'll
              share details before public launch.
            </p>
          </details>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="section cta-section">
      <div class="container">
        <div class="section-header">
          <span class="section-label">GET EARLY ACCESS</span>
        </div>
        <p class="cta-subtext">
          Be first. Early access is limited — we're rolling out to the waitlist in order.
        </p>

        <div v-if="!submitted" class="waitlist-form">
          <div class="form-row">
            <input
              v-model="email"
              type="email"
              class="email-input"
              placeholder="email@company.com"
              @keydown.enter="submitWaitlist"
              :disabled="submitting"
            />
            <button class="cta-btn" @click="submitWaitlist" :disabled="submitting">
              {{ submitting ? "JOINING..." : "JOIN THE WAITLIST →" }}
            </button>
          </div>
          <p v-if="formError" class="form-error">{{ formError }}</p>
          <p class="form-micro">✦ {{ waitlistCount }} developers waiting · No spam, ever</p>
        </div>

        <div v-else class="submitted-state">
          <span class="submitted-icon">✦</span>
          <p class="submitted-text">You're on the list! We'll be in touch before launch.</p>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <div class="container">
        <div class="footer-inner">
          <div class="footer-brand">
            <div class="logo">
              <div class="logo-mark logo-mark-sm" />
              <span class="logo-label">SMARTPROMPT</span>
            </div>
            <p class="footer-tagline">The AI layer your app was always missing.</p>
          </div>
          <div class="footer-links">
            <span class="footer-copy">© 2026 SmartPrompt</span>
            <span class="footer-sep">·</span>
            <a href="#" class="footer-link">Privacy</a>
            <span class="footer-sep">·</span>
            <a href="#" class="footer-link">Terms</a>
            <span class="footer-sep">·</span>
            <a href="/users/sign_in" class="footer-link">SIGN IN</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue"

const email = ref("")
const submitting = ref(false)
const submitted = ref(false)
const formError = ref("")
const waitlistCount = ref(847)
const isScrolled = ref(false)

function onScroll() {
  isScrolled.value = window.scrollY > 20
}

onMounted(() => {
  window.addEventListener("scroll", onScroll)
})

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll)
})

async function submitWaitlist() {
  formError.value = ""
  if (!email.value.trim()) {
    formError.value = "Please enter your email address."
    return
  }
  submitting.value = true
  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch("/api/v1/waitlist", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken || "",
      },
      body: JSON.stringify({ email: email.value }),
    })
    const data = await response.json()
    if (response.ok) {
      waitlistCount.value = data.position
      submitted.value = true
    } else {
      formError.value = data.error || "Something went wrong. Please try again."
    }
  } catch {
    formError.value = "Network error. Please try again."
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  color: #e8e6e3;
  position: relative;
  overflow-x: hidden;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.hero-glow {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background: radial-gradient(ellipse 60% 40% at 50% -10%, rgba(245, 166, 35, 0.15), transparent);
}

/* NAVBAR */
.navbar {
  position: sticky;
  top: 0;
  z-index: 100;
  transition: background 0.2s, border-color 0.2s;
  border-bottom: 1px solid transparent;
}

.navbar.scrolled {
  background: rgba(10, 10, 11, 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.nav-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-mark {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #f5a623 0%, #e8731a 100%);
  border-radius: 6px;
  flex-shrink: 0;
}

.logo-mark-sm {
  width: 22px;
  height: 22px;
  border-radius: 4px;
}

.logo-label {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #e8e6e3;
}

.sign-in-btn {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: rgba(232, 230, 227, 0.6);
  text-decoration: none;
  padding: 7px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  transition: color 0.15s, border-color 0.15s;
}

.sign-in-btn:hover {
  color: #f5a623;
  border-color: rgba(245, 166, 35, 0.3);
}

/* CONTAINER */
.container {
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
}

/* HERO */
.hero {
  padding: 80px 0 60px;
  text-align: center;
}

.eyebrow {
  margin-bottom: 24px;
}

.eyebrow-tag {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #f5a623;
  background: rgba(245, 166, 35, 0.1);
  border: 1px solid rgba(245, 166, 35, 0.2);
  border-radius: 100px;
  padding: 5px 14px;
}

.headline {
  font-family: var(--font-display, var(--font-mono));
  font-size: clamp(36px, 6vw, 64px);
  font-weight: 700;
  line-height: 1.1;
  color: #e8e6e3;
  margin: 0 0 20px;
  letter-spacing: -1px;
}

.subheadline {
  font-family: var(--font-mono);
  font-size: 14px;
  line-height: 1.7;
  color: rgba(232, 230, 227, 0.55);
  max-width: 560px;
  margin: 0 auto 36px;
}

/* WAITLIST FORM */
.waitlist-form {
  max-width: 560px;
  margin: 0 auto;
}

.form-row {
  display: flex;
  gap: 8px;
}

.email-input {
  flex: 1;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 12px 16px;
  font-family: var(--font-mono);
  font-size: 13px;
  color: #e8e6e3;
  outline: none;
  transition: border-color 0.15s;
  min-width: 0;
}

.email-input:focus {
  border-color: rgba(245, 166, 35, 0.5);
}

.email-input::placeholder {
  color: rgba(232, 230, 227, 0.25);
}

.email-input:disabled {
  opacity: 0.5;
}

.cta-btn {
  background: #f5a623;
  border: none;
  border-radius: 4px;
  padding: 12px 20px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}

.cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.cta-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-error {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #f87171;
  margin: 8px 0 0;
  text-align: left;
}

.form-micro {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.3);
  margin: 10px 0 0;
  letter-spacing: 0.5px;
}

/* SUBMITTED STATE */
.submitted-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  max-width: 560px;
  margin: 0 auto;
  padding: 18px 24px;
  background: rgba(245, 166, 35, 0.06);
  border: 1px solid rgba(245, 166, 35, 0.2);
  border-radius: 6px;
}

.submitted-icon {
  color: #f5a623;
  font-size: 16px;
}

.submitted-text {
  font-family: var(--font-mono);
  font-size: 13px;
  color: rgba(232, 230, 227, 0.8);
  margin: 0;
}

/* WAITLIST COUNTER */
.waitlist-counter {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.counter-dot {
  color: #f5a623;
  font-size: 12px;
}

.counter-text {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.4);
  letter-spacing: 0.5px;
}

/* PROOF STRIP */
.proof-strip {
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 24px 0;
  background: rgba(255, 255, 255, 0.015);
}

.proof-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.proof-stat {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.4);
  letter-spacing: 0.5px;
}

.proof-stat em {
  font-style: normal;
  color: #f5a623;
  font-weight: 700;
}

.proof-divider {
  color: rgba(255, 255, 255, 0.15);
  font-size: 14px;
}

/* SECTIONS */
.section {
  position: relative;
  z-index: 1;
  padding: 80px 0;
}

.section-alt {
  background: rgba(255, 255, 255, 0.015);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.section-header {
  margin-bottom: 48px;
  text-align: center;
}

.section-label {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2.5px;
  color: rgba(232, 230, 227, 0.35);
}

/* PROBLEM GRID */
.problem-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.problem-card {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 24px;
}

.problem-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #f5a623;
  display: block;
  margin-bottom: 12px;
}

.problem-text {
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.7;
  color: rgba(232, 230, 227, 0.5);
  margin: 0;
}

/* BENTO GRID */
.bento-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.bento-cell {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 24px;
  transition: border-color 0.15s;
}

.bento-cell:hover {
  border-color: rgba(245, 166, 35, 0.15);
}

.bento-wide {
  grid-column: span 2;
}

.bento-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #f5a623;
  display: block;
  margin-bottom: 12px;
}

.bento-text {
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.7;
  color: rgba(232, 230, 227, 0.5);
  margin: 0;
}

/* STEPS */
.steps-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  gap: 0;
  align-items: center;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
}

.step-number {
  font-family: var(--font-mono);
  font-size: 28px;
  font-weight: 700;
  color: rgba(245, 166, 35, 0.2);
  line-height: 1;
  flex-shrink: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.step-title {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #e8e6e3;
}

.step-text {
  font-family: var(--font-mono);
  font-size: 11px;
  line-height: 1.7;
  color: rgba(232, 230, 227, 0.45);
  margin: 0;
}

.step-connector {
  width: 40px;
  height: 1px;
  background: linear-gradient(90deg, rgba(245, 166, 35, 0.3), rgba(245, 166, 35, 0.1));
  flex-shrink: 0;
  margin: 0 8px;
  align-self: flex-start;
  margin-top: 14px;
}

/* FAQ */
.faq-list {
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.faq-item {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  overflow: hidden;
}

.faq-question {
  display: block;
  padding: 18px 22px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  color: rgba(232, 230, 227, 0.75);
  cursor: pointer;
  list-style: none;
  user-select: none;
  transition: color 0.15s;
}

.faq-question::-webkit-details-marker {
  display: none;
}

.faq-question::after {
  content: "+";
  float: right;
  color: rgba(245, 166, 35, 0.5);
  font-size: 16px;
  line-height: 1;
  transition: transform 0.2s;
}

.faq-item[open] .faq-question::after {
  transform: rotate(45deg);
}

.faq-item[open] .faq-question {
  color: #e8e6e3;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.faq-answer {
  padding: 16px 22px;
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.7;
  color: rgba(232, 230, 227, 0.5);
  margin: 0;
}

.faq-answer code {
  background: rgba(245, 166, 35, 0.1);
  border: 1px solid rgba(245, 166, 35, 0.15);
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 11px;
  color: #f5a623;
}

/* CTA SECTION */
.cta-section {
  text-align: center;
}

.cta-subtext {
  font-family: var(--font-mono);
  font-size: 13px;
  color: rgba(232, 230, 227, 0.45);
  margin: 0 0 32px;
}

/* FOOTER */
.footer {
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 32px 0;
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.footer-tagline {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
  margin: 0;
  letter-spacing: 0.3px;
}

.footer-links {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.footer-copy {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.footer-sep {
  color: rgba(255, 255, 255, 0.1);
  font-size: 12px;
}

.footer-link {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.3);
  text-decoration: none;
  letter-spacing: 0.5px;
  transition: color 0.15s;
}

.footer-link:hover {
  color: #f5a623;
}

/* MOBILE */
@media (max-width: 768px) {
  .hero {
    padding: 60px 0 40px;
  }

  .form-row {
    flex-direction: column;
  }

  .cta-btn {
    width: 100%;
  }

  .problem-grid {
    grid-template-columns: 1fr;
  }

  .bento-grid {
    grid-template-columns: 1fr;
  }

  .bento-wide {
    grid-column: span 1;
  }

  .steps-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .step-connector {
    display: none;
  }

  .proof-stats {
    flex-direction: column;
    gap: 8px;
  }

  .proof-divider {
    display: none;
  }

  .footer-inner {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
